import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpecialistAuth } from '../../context/SpecialistAuthContext';
import { API_BASE_URL } from '../../config/api';
import SpecialistDashboardLayout from '../../components/SpecialistDashboardLayout';
import '../SellerDashboardPage/SellerDashboardPage.css';
import './SpecialistDashboardPage.css';

const SpecialistDashboardPage = () => {
  const { specialist } = useSpecialistAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRFQ: 0,
    totalInquiries: 0,
    totalReplied: 0,
    totalProjects: 0
  });
  const [rfqList, setRfqList] = useState([]);
  const [selectedRFQs, setSelectedRFQs] = useState([]);

  // Get current month formatted for dropdown
  const getCurrentMonthFormatted = () => {
    const now = new Date();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[now.getMonth()]}, ${now.getFullYear()}`;
  };

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthFormatted());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [chartData, setChartData] = useState({
    inquireReceived: 0,
    messageReceived: 0
  });

  // Month options for dropdown
  const monthOptions = [
    'Jan, 2025', 'Feb, 2025', 'Mar, 2025', 'Apr, 2025', 'May, 2025', 'Jun, 2025',
    'Jul, 2025', 'Aug, 2025', 'Sep, 2025', 'Oct, 2025', 'Nov, 2025', 'Dec, 2025',
    'Jan, 2026', 'Feb, 2026', 'Mar, 2026', 'Apr, 2026', 'May, 2026', 'Jun, 2026',
    'Jul, 2026', 'Aug, 2026', 'Sep, 2026', 'Oct, 2026', 'Nov, 2026', 'Dec, 2026'
  ];

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const specialistId = specialist?.id;
      const token = localStorage.getItem('specialist_token');

      // Parse selectedMonth to get month/year for filtering
      const [monthStr, yearStr] = selectedMonth.split(', ');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = monthNames.indexOf(monthStr) + 1;
      const year = parseInt(yearStr);

      // Build headers with authentication token
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      // Build query params
      const queryParams = new URLSearchParams();
      if (specialistId) queryParams.append('specialistId', specialistId);
      if (monthIndex) queryParams.append('month', monthIndex);
      if (year) queryParams.append('year', year);
      const queryString = queryParams.toString();

      // Fetch all data in parallel using specialist-specific endpoints
      const [statsResponse, rfqResponse, analyticsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/specialist-auth/dashboard/stats?${queryString}`, { headers }),
        fetch(`${API_BASE_URL}/rfq?limit=5&status=new`, { headers: { 'Content-Type': 'application/json' } }),
        fetch(`${API_BASE_URL}/specialist-auth/dashboard/analytics?${queryString}`, { headers })
      ]);

      // Parse stats response
      let statsData = {};
      try { statsData = await statsResponse.json(); } catch (e) { console.error('Error parsing stats:', e); }

      // Parse analytics response
      let analyticsData = {};
      try { analyticsData = await analyticsResponse.json(); } catch (e) { console.error('Error parsing analytics:', e); }

      // Parse RFQ response
      let rfqData = [];
      try { rfqData = await rfqResponse.json(); } catch (e) { console.error('Error parsing RFQ:', e); }

      // Set stats
      if (statsData.summary) {
        setStats({
          totalRFQ: statsData.summary.totalRFQReceived || 0,
          totalInquiries: statsData.summary.totalInquireMessage || 0,
          totalReplied: statsData.summary.totalReplied || 0,
          totalProjects: statsData.summary.totalProjectListing || statsData.products?.total || 0
        });
      } else {
        const rfqCount = Array.isArray(rfqData) ? rfqData.length : (rfqData?.rfqs?.length || rfqData?.total || 0);
        setStats({
          totalRFQ: rfqCount,
          totalInquiries: analyticsData?.inquiries || 0,
          totalReplied: 0,
          totalProjects: 0
        });
      }

      // Set chart data
      if (analyticsData) {
        setChartData({
          inquireReceived: analyticsData.inquiriesReceived || analyticsData.messages || 0,
          messageReceived: analyticsData.messages || 0
        });
      }

      // Set RFQ list
      if (Array.isArray(rfqData) && rfqData.length > 0) {
        setRfqList(rfqData.slice(0, 5));
      } else if (rfqData?.rfqs && rfqData.rfqs.length > 0) {
        setRfqList(rfqData.rfqs.slice(0, 5));
      } else {
        setRfqList([]);
      }
    } catch (error) {
      console.error('Error fetching specialist dashboard data:', error);
      setStats({ totalRFQ: 0, totalInquiries: 0, totalReplied: 0, totalProjects: 0 });
      setChartData({ inquireReceived: 0, messageReceived: 0 });
      setRfqList([]);
    } finally {
      setLoading(false);
    }
  }, [specialist, selectedMonth]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRFQs(rfqList.map(rfq => rfq.id));
    } else {
      setSelectedRFQs([]);
    }
  };

  const handleSelectRFQ = (rfqId) => {
    if (selectedRFQs.includes(rfqId)) {
      setSelectedRFQs(selectedRFQs.filter(id => id !== rfqId));
    } else {
      setSelectedRFQs([...selectedRFQs, rfqId]);
    }
  };

  const handleViewRFQ = (rfqId) => {
    navigate('/specialist-rfq');
  };

  // Navigation handlers for stat cards
  const handleStatCardClick = (type) => {
    switch(type) {
      case 'rfq':
        navigate('/specialist-rfq');
        break;
      case 'inquiries':
        navigate('/specialist-messages');
        break;
      case 'replied':
        navigate('/specialist-rfq');
        break;
      case 'projects':
        navigate('/specialist-projects');
        break;
      default:
        break;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.');
  };

  return (
    <SpecialistDashboardLayout>
      <div className="seller-dashboard-content">
        <div className="dashboard-welcome-section">
          <div className="welcome-content">
            <h1 className="dashboard-title">Specialist Dashboard</h1>
            <p className="dashboard-subtitle">Control projects, monitor progress, and deliver results with ease.</p>
          </div>
        </div>

        <div className="stats-cards-container" style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }}>
          <div className="stat-card clickable" style={{ background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }} onClick={() => handleStatCardClick('rfq')} title="View RFQ Manage">
            <div className="stat-card-inner">
              <div className="stat-icon rfq-icon">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M23.3756 11.4375C23.3756 11.2883 23.3163 11.1452 23.2108 11.0398C23.1054 10.9343 22.9623 10.875 22.8131 10.875H6.70703C6.55785 10.875 6.41477 10.9343 6.30928 11.0398C6.20379 11.1452 6.14453 11.2883 6.14453 11.4375C6.14453 11.5867 6.20379 11.7298 6.30928 11.8352C6.41477 11.9407 6.55785 12 6.70703 12H22.8131C22.9623 12 23.1054 11.9407 23.2108 11.8352C23.3163 11.7298 23.3756 11.5867 23.3756 11.4375ZM20.891 15.2602C20.891 15.1111 20.8318 14.968 20.7263 14.8625C20.6208 14.757 20.4777 14.6977 20.3285 14.6977H6.70703C6.55785 14.6977 6.41477 14.757 6.30928 14.8625C6.20379 14.968 6.14453 15.1111 6.14453 15.2602C6.14453 15.4094 6.20379 15.5525 6.30928 15.658C6.41477 15.7635 6.55785 15.8227 6.70703 15.8227H20.3285C20.4777 15.8227 20.6208 15.7635 20.7263 15.658C20.8318 15.5525 20.891 15.4094 20.891 15.2602ZM6.70703 19.6449H19.2468C19.396 19.6449 19.5391 19.5857 19.6446 19.4802C19.7501 19.3747 19.8093 19.2316 19.8093 19.0824C19.8093 18.9333 19.7501 18.7902 19.6446 18.6847C19.5391 18.5792 19.396 18.5199 19.2468 18.5199H6.70703C6.55785 18.5199 6.41477 18.5792 6.30928 18.6847C6.20379 18.7902 6.14453 18.9333 6.14453 19.0824C6.14453 19.2316 6.20379 19.3747 6.30928 19.4802C6.41477 19.5857 6.55785 19.6449 6.70703 19.6449ZM6.70703 23.4677H19.3751C19.5243 23.4677 19.6674 23.4084 19.7728 23.3029C19.8783 23.1974 19.9376 23.0544 19.9376 22.9052C19.9376 22.756 19.8783 22.6129 19.7728 22.5074C19.6674 22.402 19.5243 22.3427 19.3751 22.3427H6.70703C6.55785 22.3427 6.41477 22.402 6.30928 22.5074C6.20379 22.6129 6.14453 22.756 6.14453 22.9052C6.14453 23.0544 6.20379 23.1974 6.30928 23.3029C6.41477 23.4084 6.55785 23.4677 6.70703 23.4677Z" fill="#003654"/>
                  <path d="M29.9537 17.1241C29.8045 17.1241 29.6614 17.1834 29.5559 17.2888C29.4505 17.3943 29.3912 17.5374 29.3912 17.6866V26.5994C29.3907 26.9757 29.2411 27.3364 28.975 27.6024C28.709 27.8685 28.3483 28.0181 27.972 28.0186H11.2494C11.1001 28.0192 10.957 28.0786 10.8512 28.184L7.31475 31.7204V28.5811C7.31475 28.4319 7.25549 28.2888 7.15 28.1833C7.04451 28.0779 6.90143 28.0186 6.75225 28.0186H3.66356C3.28801 28.0176 2.92822 27.8675 2.66319 27.6014C2.39816 27.3353 2.24955 26.975 2.25 26.5994V7.75903C2.24955 7.38348 2.39816 7.02309 2.66319 6.75701C2.92822 6.49092 3.28801 6.34089 3.66356 6.33984H26.0314C26.1806 6.33984 26.3236 6.28058 26.4291 6.17509C26.5346 6.0696 26.5939 5.92653 26.5939 5.77734C26.5939 5.62816 26.5346 5.48508 26.4291 5.3796C26.3236 5.27411 26.1806 5.21484 26.0314 5.21484H3.66356C2.98978 5.21633 2.3441 5.48504 1.86819 5.962C1.39227 6.43897 1.125 7.08524 1.125 7.75903V26.5994C1.125 27.2732 1.39227 27.9195 1.86819 28.3964C2.3441 28.8734 2.98978 29.1421 3.66356 29.1436H6.18975V33.0783C6.18977 33.1895 6.22278 33.2982 6.28459 33.3907C6.3464 33.4832 6.43424 33.5553 6.53701 33.5979C6.63978 33.6404 6.75286 33.6516 6.86196 33.6299C6.97105 33.6082 7.07127 33.5546 7.14994 33.476L11.4823 29.1436H27.9714C28.646 29.1428 29.2927 28.8746 29.7696 28.3976C30.2466 27.9206 30.5149 27.2739 30.5156 26.5994V17.6866C30.5156 17.5374 30.4564 17.3943 30.3509 17.2888C30.2454 17.1834 30.1029 17.1241 29.9537 17.1241Z" fill="#003654"/>
                  <path d="M34.0994 3.90746L31.5153 2.52765C31.3386 2.43347 31.1443 2.37695 30.9447 2.36166C30.7451 2.34636 30.5444 2.37262 30.3554 2.43877C29.975 2.5699 29.6589 2.84131 29.4718 3.19759L28.5819 4.86315C28.5751 4.87384 28.5695 4.88452 28.5633 4.89577L27.7055 6.50227C27.6994 6.51299 27.6939 6.52408 27.6892 6.53546L21.4218 18.2709C21.3703 18.3668 21.348 18.4758 21.3577 18.5842L21.7334 22.9531C21.7415 23.0464 21.7728 23.1362 21.8243 23.2144C21.8759 23.2925 21.9461 23.3565 22.0287 23.4007C22.1113 23.4448 22.2035 23.4676 22.2971 23.4671C22.3908 23.4665 22.4828 23.4426 22.5648 23.3975L26.4078 21.282C26.5034 21.2297 26.5815 21.1505 26.6323 21.0541L34.6828 5.98084C35.0861 5.22484 34.8245 4.29446 34.0994 3.90746ZM33.6905 5.45096L25.7188 20.3769L22.7791 21.9947L22.4917 18.654L28.6978 7.03327C28.7039 7.02256 28.7093 7.01147 28.7141 7.00009L29.537 5.45884C29.5438 5.44815 29.5494 5.43746 29.5556 5.42621L30.4629 3.72746C30.5166 3.62318 30.6078 3.54317 30.7183 3.50359C30.7615 3.48821 30.8074 3.48173 30.8532 3.48454C30.899 3.48734 30.9438 3.49937 30.9849 3.5199L33.569 4.90027C33.7468 4.99534 33.8008 5.24284 33.6899 5.45152L33.6905 5.45096Z" fill="#003654"/>
                </svg>
              </div>
              <div className="stat-info">
                <span className="stat-label">Total RFQ Received</span>
                <span className="stat-value">{loading ? '...' : stats.totalRFQ}</span>
              </div>
            </div>
          </div>

          <div className="stat-card clickable" style={{ background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }} onClick={() => handleStatCardClick('inquiries')} title="View Inquiries & Messages">
            <div className="stat-card-inner">
              <div className="stat-icon message-icon">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M30.375 11.25H25.875V6.75C25.875 6.15326 25.6379 5.58097 25.216 5.15901C24.794 4.73705 24.2217 4.5 23.625 4.5H5.625C5.02826 4.5 4.45597 4.73705 4.03401 5.15901C3.61205 5.58097 3.375 6.15326 3.375 6.75V24.75C3.37566 24.9617 3.43601 25.1688 3.54912 25.3477C3.66224 25.5266 3.82352 25.67 4.01444 25.7613C4.20536 25.8527 4.41819 25.8883 4.62845 25.8641C4.83872 25.84 5.03791 25.757 5.20312 25.6247L10.125 21.6562V25.875C10.125 26.4717 10.3621 27.044 10.784 27.466C11.206 27.8879 11.7783 28.125 12.375 28.125H25.5361L30.7969 32.3747C30.9959 32.5357 31.244 32.624 31.5 32.625C31.7984 32.625 32.0845 32.5065 32.2955 32.2955C32.5065 32.0845 32.625 31.7984 32.625 31.5V13.5C32.625 12.9033 32.3879 12.331 31.966 11.909C31.544 11.4871 30.9717 11.25 30.375 11.25ZM9.35859 19.3753L5.625 22.3945V6.75H23.625V19.125H10.0659C9.80846 19.125 9.55878 19.2134 9.35859 19.3753ZM30.375 29.1445L26.6414 26.1253C26.4423 25.9643 26.1943 25.876 25.9383 25.875H12.375V21.375H23.625C24.2217 21.375 24.794 21.1379 25.216 20.716C25.6379 20.294 25.875 19.7217 25.875 19.125V13.5H30.375V29.1445Z" fill="#003654"/>
                </svg>
              </div>
              <div className="stat-info">
                <span className="stat-label">Total Inquire & Message</span>
                <span className="stat-value">{loading ? '...' : stats.totalInquiries}</span>
              </div>
            </div>
          </div>

          <div className="stat-card clickable" style={{ background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }} onClick={() => handleStatCardClick('replied')} title="View Replied RFQs">
            <div className="stat-card-inner">
              <div className="stat-icon replied-icon">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_specialist_replied)">
                    <path d="M35.2496 34.5001C35.1224 34.5001 34.9973 34.4678 34.886 34.4061C34.7748 34.3444 34.681 34.2555 34.6136 34.1476C33.5385 32.4151 32.0378 30.9867 30.2545 29.9981C28.4712 29.0096 26.4646 28.4939 24.4256 28.5001H23.9996V32.2501C23.9999 32.3986 23.956 32.5439 23.8736 32.6675C23.7913 32.7911 23.674 32.8874 23.5368 32.9443C23.3996 33.0012 23.2486 33.016 23.1029 32.987C22.9573 32.9579 22.8235 32.8863 22.7186 32.7811L15.2186 25.2811C15.078 25.1405 14.999 24.9497 14.999 24.7509C14.999 24.552 15.078 24.3613 15.2186 24.2206L22.7186 16.7206C22.8233 16.6153 22.957 16.5436 23.1026 16.5147C23.2483 16.4858 23.3992 16.5011 23.5361 16.5586C23.6733 16.6149 23.7906 16.7107 23.8731 16.8338C23.9557 16.957 23.9997 17.1019 23.9996 17.2501V21.0226C30.6821 21.4126 35.9996 26.9716 35.9996 33.7501C35.9996 33.949 35.9206 34.1398 35.7799 34.2804C35.6393 34.4211 35.4485 34.5001 35.2496 34.5001ZM23.2496 27.0001H24.4256C26.2542 26.9943 28.0647 27.3619 29.7461 28.0805C31.4276 28.7991 32.9446 29.8535 34.2041 31.1791C33.0371 26.2111 28.5686 22.5001 23.2496 22.5001C23.0507 22.5001 22.8599 22.4211 22.7193 22.2804C22.5786 22.1398 22.4996 21.949 22.4996 21.7501V19.0606L16.8101 24.7501L22.4996 30.4396V27.7501C22.4996 27.5512 22.5786 27.3604 22.7193 27.2198C22.8599 27.0791 23.0507 27.0001 23.2496 27.0001Z" fill="#003654"/>
                    <path d="M14.25 22.5H3C1.3455 22.5 0 21.1545 0 19.5V4.5C0 2.8455 1.3455 1.5 3 1.5H30C31.6545 1.5 33 2.8455 33 4.5V18.75C33 18.9489 32.921 19.1397 32.7803 19.2803C32.6397 19.421 32.4489 19.5 32.25 19.5C32.0511 19.5 31.8603 19.421 31.7197 19.2803C31.579 19.1397 31.5 18.9489 31.5 18.75V4.5C31.5 3.6735 30.8265 3 30 3H3C2.1735 3 1.5 3.6735 1.5 4.5V19.5C1.5 20.3265 2.1735 21 3 21H14.25C14.4489 21 14.6397 21.079 14.7803 21.2197C14.921 21.3603 15 21.5511 15 21.75C15 21.9489 14.921 22.1397 14.7803 22.2803C14.6397 22.421 14.4489 22.5 14.25 22.5Z" fill="#003654"/>
                    <path d="M16.5001 14.367C15.9511 14.367 15.4021 14.2425 14.9011 13.995L0.417096 6.822C0.238671 6.73388 0.10256 6.57849 0.0387034 6.39002C-0.0251528 6.20154 -0.0115225 5.99542 0.0765957 5.817C0.164714 5.63857 0.320102 5.50246 0.508576 5.43861C0.69705 5.37475 0.903171 5.38838 1.0816 5.4765L15.5641 12.6495C16.1506 12.939 16.8466 12.939 17.4331 12.6495L31.9156 5.4765C32.0039 5.43287 32.1 5.40706 32.1983 5.40056C32.2967 5.39406 32.3953 5.40699 32.4886 5.43861C32.5819 5.47022 32.6681 5.51991 32.7422 5.58484C32.8164 5.64976 32.877 5.72865 32.9206 5.817C32.9642 5.90534 32.99 6.00142 32.9965 6.09974C33.003 6.19806 32.9901 6.29669 32.9585 6.39002C32.9269 6.48334 32.8772 6.56952 32.8123 6.64364C32.7473 6.71776 32.6684 6.77837 32.5801 6.822L18.0991 13.995C17.5981 14.2425 17.0491 14.367 16.5001 14.367Z" fill="#003654"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_specialist_replied">
                      <rect width="36" height="36" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              </div>
              <div className="stat-info">
                <span className="stat-label">Total Replied</span>
                <span className="stat-value">{loading ? '...' : stats.totalReplied}</span>
              </div>
            </div>
          </div>

          <div className="stat-card clickable" style={{ background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }} onClick={() => handleStatCardClick('projects')} title="View Project Listing">
            <div className="stat-card-inner">
              <div className="stat-icon products-icon">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.6816 7.31819H25.1557V15.3113C25.1557 15.4605 25.215 15.6036 25.3204 15.7091C25.4259 15.8145 25.569 15.8738 25.7182 15.8738C25.8674 15.8738 26.0104 15.8145 26.1159 15.7091C26.2214 15.6036 26.2807 15.4605 26.2807 15.3113V6.75569C26.2807 6.6065 26.2214 6.46343 26.1159 6.35794C26.0104 6.25245 25.8674 6.19319 25.7182 6.19319H22.0844C22.1086 6.08856 22.1244 5.98056 22.1244 5.86862V4.78131H27.4338C27.8337 4.78131 28.1594 5.11206 28.1594 5.51819V15.3113C28.1594 15.4605 28.2187 15.6036 28.3242 15.7091C28.4297 15.8145 28.5728 15.8738 28.7219 15.8738C28.8711 15.8738 29.0142 15.8145 29.1197 15.7091C29.2252 15.6036 29.2844 15.4605 29.2844 15.3113V5.51819C29.2844 4.49162 28.4542 3.65631 27.4338 3.65631H22.103C22.0006 2.9515 21.4016 2.407 20.6731 2.407H18.0046C17.8222 2.02456 17.5355 1.70137 17.1776 1.47459C16.8197 1.24782 16.405 1.12666 15.9813 1.12506C15.5563 1.12188 15.1393 1.24106 14.7801 1.46836C14.421 1.69567 14.1348 2.02151 13.9558 2.407H11.2889C10.9392 2.40791 10.6015 2.53464 10.3375 2.76402C10.0735 2.99341 9.9009 3.31014 9.85119 3.65631H4.50688C3.4865 3.65631 2.65625 4.49162 2.65625 5.51819V33.0244C2.65685 33.5151 2.85201 33.9854 3.19894 34.3324C3.54587 34.6793 4.01624 34.8745 4.50688 34.8751H12.7306C12.8798 34.8751 13.0229 34.8158 13.1284 34.7103C13.2339 34.6048 13.2931 34.4617 13.2931 34.3126C13.2931 34.1634 13.2339 34.0203 13.1284 33.9148C13.0229 33.8093 12.8798 33.7501 12.7306 33.7501H4.50688C4.10694 33.7501 3.78125 33.4244 3.78125 33.0244V5.51819C3.78125 5.11206 4.10694 4.78131 4.50688 4.78131H9.82812V5.86694C9.82812 5.97944 9.84388 6.088 9.86863 6.19319H6.2225C6.07332 6.19319 5.93024 6.25245 5.82475 6.35794C5.71926 6.46343 5.66 6.6065 5.66 6.75569V31.0613C5.66 31.2105 5.71926 31.3536 5.82475 31.4591C5.93024 31.5645 6.07332 31.6238 6.2225 31.6238H12.7306C12.8798 31.6238 13.0229 31.5645 13.1284 31.4591C13.2339 31.3536 13.2931 31.2105 13.2931 31.0613C13.2931 30.9121 13.2339 30.7691 13.1284 30.6636C13.0229 30.5581 12.8798 30.4988 12.7306 30.4988H6.785V7.31819H11.2816L11.2901 7.31875H20.6742L20.6816 7.31819ZM10.9559 3.86612C10.9559 3.68162 11.1061 3.532 11.2901 3.532H14.3568C14.6195 3.532 14.8473 3.35031 14.9058 3.09437C15.0183 2.59769 15.461 2.25062 15.9824 2.25062C16.4949 2.25062 16.9483 2.60556 17.0596 3.09494C17.0879 3.21923 17.1576 3.33023 17.2572 3.40974C17.3569 3.48925 17.4806 3.53256 17.6081 3.53256H20.6742C20.7177 3.53262 20.7607 3.54137 20.8008 3.55829C20.8408 3.57521 20.8771 3.59996 20.9074 3.63109C20.9377 3.66221 20.9616 3.69909 20.9775 3.73955C20.9933 3.78001 21.001 3.82323 20.9999 3.86669V4.19237C20.9999 4.20194 20.9943 4.20981 20.9943 4.21937V5.8675C20.9932 5.95398 20.9597 6.0369 20.9004 6.09981L20.8908 6.10825C20.87 6.12654 20.847 6.14204 20.8222 6.15437L20.7929 6.17069C20.7578 6.18452 20.7205 6.19213 20.6827 6.19319L20.6737 6.19431H11.2856C11.2524 6.19431 11.222 6.18475 11.1916 6.17519C11.1815 6.17237 11.1708 6.17294 11.1613 6.169C11.1015 6.14462 11.0503 6.10329 11.0138 6.05011C10.9773 5.99693 10.9572 5.93423 10.9559 5.86975V3.86725V3.86612Z" fill="#003654"/>
                  <path d="M14.418 34.8751H32.7813C32.9305 34.8751 33.0736 34.8159 33.1791 34.7104C33.2846 34.6049 33.3438 34.4618 33.3438 34.3126V17C33.3438 16.8508 33.2846 16.7077 33.1791 16.6023C33.0736 16.4968 32.9305 16.4375 32.7813 16.4375H14.418C14.2688 16.4375 14.1257 16.4968 14.0202 16.6023C13.9147 16.7077 13.8555 16.8508 13.8555 17V34.3126C13.8555 34.4618 13.9147 34.6049 14.0202 34.7104C14.1257 34.8159 14.2688 34.8751 14.418 34.8751ZM25.0127 17.5625V21.9444H22.1872V17.5625H25.0127ZM14.9805 17.5625H21.0622V22.5069C21.0622 22.6561 21.1215 22.7991 21.227 22.9046C21.3325 23.0101 21.4755 23.0694 21.6247 23.0694H25.5752C25.7243 23.0694 25.8674 23.0101 25.9729 22.9046C26.0784 22.7991 26.1377 22.6561 26.1377 22.5069V17.5625H32.2194V33.7501H14.9805V17.5625Z" fill="#003654"/>
                  <path d="M13.5387 9.43836C13.4865 9.38608 13.4245 9.3446 13.3562 9.3163C13.288 9.288 13.2149 9.27344 13.141 9.27344C13.0671 9.27344 12.994 9.288 12.9257 9.3163C12.8575 9.3446 12.7955 9.38608 12.7433 9.43836L11.1745 11.0077L10.4601 10.2939C10.3546 10.1885 10.2116 10.1292 10.0624 10.1292C9.91325 10.1292 9.7702 10.1885 9.66473 10.2939C9.55926 10.3994 9.5 10.5425 9.5 10.6916C9.5 10.8408 9.55926 10.9838 9.66473 11.0893L10.7768 12.2008C10.8823 12.3063 11.0253 12.3655 11.1745 12.3655C11.3236 12.3655 11.4667 12.3063 11.5722 12.2008L13.5387 10.2337C13.5909 10.1815 13.6324 10.1196 13.6607 10.0513C13.689 9.98308 13.7036 9.90993 13.7036 9.83605C13.7036 9.76218 13.689 9.68903 13.6607 9.62079C13.6324 9.55255 13.5909 9.49056 13.5387 9.43836Z" fill="#003654"/>
                  <path d="M21.8929 10.2574H15.5254C15.3762 10.2574 15.2332 10.3166 15.1277 10.4221C15.0222 10.5276 14.9629 10.6707 14.9629 10.8199C14.9629 10.969 15.0222 11.1121 15.1277 11.2176C15.2332 11.3231 15.3762 11.3824 15.5254 11.3824H21.8929C22.0421 11.3824 22.1852 11.3231 22.2907 11.2176C22.3962 11.1121 22.4554 10.969 22.4554 10.8199C22.4554 10.6707 22.3962 10.5276 22.2907 10.4221C22.1852 10.3166 22.0421 10.2574 21.8929 10.2574Z" fill="#003654"/>
                </svg>
              </div>
              <div className="stat-info">
                <span className="stat-label">Total Project Listing</span>
                <span className="stat-value">{loading ? '...' : stats.totalProjects}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-chart-section">
          <div className="chart-main-row">
            <div className="chart-left-panel">
              <div className="chart-title-section">
                <h2 className="chart-title">Dashboard</h2>
                <p className="chart-subtitle">Overview of Latest Month</p>
              </div>
              <div className="chart-stats-block">
                <div className="chart-stat-inline">
                  <span className="stat-number">{loading ? '...' : (chartData.inquireReceived || 0)}</span>
                  <span className="stat-text">Inquire Received</span>
                </div>
                <div className="chart-stat-inline">
                  <span className="stat-number orange">{loading ? '...' : (chartData.messageReceived || 0)}</span>
                  <span className="stat-text">Message Received</span>
                </div>
              </div>
            </div>
            <div className="chart-right-panel">
              <div className="sort-by-section">
                <span className="sort-by-label">Sort by</span>
                <div className="date-picker-wrapper">
                  <button className="date-picker-btn" onClick={() => setShowDatePicker(!showDatePicker)}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.66667 5.83333V2.5M13.3333 5.83333V2.5M5.83333 9.16667H14.1667M4.16667 17.5H15.8333C16.2754 17.5 16.6993 17.3244 17.0118 17.0118C17.3244 16.6993 17.5 16.2754 17.5 15.8333V5.83333C17.5 5.39131 17.3244 4.96738 17.0118 4.65482C16.6993 4.34226 16.2754 4.16667 15.8333 4.16667H4.16667C3.72464 4.16667 3.30072 4.34226 2.98816 4.65482C2.67559 4.96738 2.5 5.39131 2.5 5.83333V15.8333C2.5 16.2754 2.67559 16.6993 2.98816 17.0118C3.30072 17.3244 3.72464 17.5 4.16667 17.5Z" stroke="#7C7D7F" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{selectedMonth}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 4.5L6 7.5L9 4.5" stroke="#7C7D7F" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  {showDatePicker && (
                    <div className="date-picker-dropdown">
                      {monthOptions.map((month) => (
                        <div 
                          key={month} 
                          className={`date-picker-option ${selectedMonth === month ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedMonth(month);
                            setShowDatePicker(false);
                          }}
                        >
                          {month}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="chart-graph-area">
                <svg viewBox="0 0 982 420" className="dashboard-chart" preserveAspectRatio="xMidYMid meet">
                  <defs>
                    <linearGradient id="blueGradientSpecialist" x1="490" y1="0" x2="490" y2="350" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#3B82F6"/>
                      <stop offset="1" stopColor="#3B82F6" stopOpacity="0"/>
                    </linearGradient>
                    <linearGradient id="orangeGradientSpecialist" x1="490" y1="0" x2="490" y2="350" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#F6A83B" stopOpacity="0.3"/>
                      <stop offset="1" stopColor="#F6A83B" stopOpacity="0"/>
                    </linearGradient>
                  </defs>
                  
                  {/* Horizontal grid lines */}
                  {[0, 65, 130, 195, 260, 325].map((y, i) => (
                    <line key={i} x1="50" y1={y + 20} x2="970" y2={y + 20} stroke="#F3F4F6" strokeWidth="1"/>
                  ))}
                  
                  {/* Vertical grid lines */}
                  {[130, 210, 290, 370, 450, 530, 610, 690, 770, 850, 930].map((x, i) => (
                    <line key={i} x1={x} y1="20" x2={x} y2="345" stroke="#D6D7D7" strokeWidth="1"/>
                  ))}
                  
                  {/* Blue area fill */}
                  <path d="M50 345 L50 260 C100 240 150 200 200 220 C250 240 280 260 320 250 C360 240 400 200 450 170 C500 140 550 160 600 180 C650 200 700 160 750 130 C800 100 850 80 900 60 C930 50 950 45 970 40 L970 345 Z" fill="url(#blueGradientSpecialist)" fillOpacity="0.5"/>
                  
                  {/* Blue line */}
                  <path d="M50 260 C100 240 150 200 200 220 C250 240 280 260 320 250 C360 240 400 200 450 170 C500 140 550 160 600 180 C650 200 700 160 750 130 C800 100 850 80 900 60 C930 50 950 45 970 40" fill="none" stroke="#1F396C" strokeWidth="2"/>
                  
                  {/* Orange area fill */}
                  <path d="M50 345 L50 200 C100 180 150 100 200 120 C250 140 280 180 320 150 C360 120 400 60 450 80 C500 100 550 140 600 120 C650 100 700 80 750 100 C800 120 850 160 900 140 C930 130 950 120 970 110 L970 345 Z" fill="url(#orangeGradientSpecialist)"/>
                  
                  {/* Orange line */}
                  <path d="M50 200 C100 180 150 100 200 120 C250 140 280 180 320 150 C360 120 400 60 450 80 C500 100 550 140 600 120 C650 100 700 80 750 100 C800 120 850 160 900 140 C930 130 950 120 970 110" fill="none" stroke="#F6A83B" strokeWidth="2"/>
                  
                  {/* Y-axis labels */}
                  <text x="40" y="25" fontSize="12" fill="#636F85" textAnchor="end" fontFamily="Inter" fontWeight="500">20k</text>
                  <text x="40" y="90" fontSize="12" fill="#636F85" textAnchor="end" fontFamily="Inter" fontWeight="500">10k</text>
                  <text x="40" y="155" fontSize="12" fill="#636F85" textAnchor="end" fontFamily="Inter" fontWeight="500">05k</text>
                  <text x="40" y="220" fontSize="12" fill="#636F85" textAnchor="end" fontFamily="Inter" fontWeight="500">03k</text>
                  <text x="40" y="285" fontSize="12" fill="#636F85" textAnchor="end" fontFamily="Inter" fontWeight="500">01k</text>
                  <text x="40" y="350" fontSize="12" fill="#636F85" textAnchor="end" fontFamily="Inter" fontWeight="500">0</text>
                  
                  {/* X-axis labels */}
                  <text x="50" y="380" fontSize="12" fill="#636F85" fontFamily="Inter">Jan</text>
                  <text x="130" y="380" fontSize="12" fill="#636F85" fontFamily="Inter">Feb</text>
                  <text x="210" y="380" fontSize="12" fill="#636F85" fontFamily="Inter">Mar</text>
                  <text x="290" y="380" fontSize="12" fill="#636F85" fontFamily="Inter">Apr</text>
                  <text x="370" y="380" fontSize="12" fill="#636F85" fontFamily="Inter">May</text>
                  <text x="450" y="380" fontSize="12" fill="#636F85" fontFamily="Inter">Jun</text>
                  <text x="530" y="380" fontSize="12" fill="#636F85" fontFamily="Inter">Jul</text>
                  <text x="610" y="380" fontSize="12" fill="#636F85" fontFamily="Inter">Aug</text>
                  <text x="690" y="380" fontSize="12" fill="#636F85" fontFamily="Inter">Sep</text>
                  <text x="770" y="380" fontSize="12" fill="#636F85" fontFamily="Inter">Oct</text>
                  <text x="850" y="380" fontSize="12" fill="#636F85" fontFamily="Inter">Nov</text>
                  <text x="930" y="380" fontSize="12" fill="#636F85" fontFamily="Inter">Dec</text>
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Figma New RFQ Table */}
        <div className="figma-rfq-table-wrapper">
          <h2 className="figma-rfq-title">New RFQ</h2>
          <div className="figma-rfq-table">
            {/* Table Header */}
            <div className="figma-rfq-header">
              <div className="figma-th figma-th-checkbox">
                <label className="custom-checkbox-wrapper">
                  <input 
                    type="checkbox" 
                    checked={selectedRFQs.length === rfqList.length && rfqList.length > 0}
                    onChange={handleSelectAll}
                  />
                  <span className="checkmark"></span>
                </label>
                <span>All</span>
              </div>
              <div className="figma-th">Date</div>
              <div className="figma-th">Buyer Name</div>
              <div className="figma-th">Company Type</div>
              <div className="figma-th">Category</div>
              <div className="figma-th">Sub Category</div>
              <div className="figma-th">Location</div>
              <div className="figma-th figma-th-email">Email</div>
              <div className="figma-th figma-th-status">Status</div>
              <div className="figma-th figma-th-details">Details</div>
            </div>
            
            {/* Table Body */}
            <div className="figma-rfq-body">
              {loading ? (
                <div className="figma-rfq-row figma-loading-row">
                  <div className="loading-spinner"></div>
                  <span>Loading...</span>
                </div>
              ) : rfqList.length > 0 ? (
                rfqList.map((rfq) => (
                  <div key={rfq.id} className="figma-rfq-row">
                    <div className="figma-td figma-td-checkbox">
                      <label className="custom-checkbox-wrapper">
                        <input 
                          type="checkbox" 
                          checked={selectedRFQs.includes(rfq.id)}
                          onChange={() => handleSelectRFQ(rfq.id)}
                        />
                        <span className="checkmark"></span>
                      </label>
                    </div>
                    <div className="figma-td">{formatDate(rfq.createdAt) || rfq.date || '-'}</div>
                    <div className="figma-td figma-td-ellipsis">{rfq.companyName || rfq.brandName || rfq.fullName || '-'}</div>
                    <div className="figma-td">{rfq.companyType || '-'}</div>
                    <div className="figma-td">{rfq.category || '-'}</div>
                    <div className="figma-td">{rfq.subCategory || '-'}</div>
                    <div className="figma-td">{rfq.country || rfq.location || '-'}</div>
                    <div className="figma-td figma-td-email figma-td-ellipsis">{rfq.email || '-'}</div>
                    <div className="figma-td figma-td-status">
                      <span className={`figma-status-badge ${rfq.status === 'replied' ? 'replied' : 'new'}`}>
                        {rfq.status === 'replied' ? 'Replied' : 'New'}
                      </span>
                    </div>
                    <div className="figma-td figma-td-details">
                      <button 
                        className="figma-details-link"
                        onClick={() => handleViewRFQ(rfq.id)}
                      >
                        Details
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="figma-rfq-row figma-empty-row">
                  <span>No RFQs found</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SpecialistDashboardLayout>
  );
};

export default SpecialistDashboardPage;
